import React, { useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Dropdown, Pagination, Spin, Table, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';

import CalculateCommissionAffiliate from 'components/CalculateCommissionAffiliate';
import { tap } from 'rxjs/operators';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Truncate from 'v2/components/Shared/TruncateText';
import { EStatusAffiliation } from 'v2/interfaces/affiliation';
import type { IAffiliation } from 'v2/interfaces/affiliation';
import affiliationsService from 'v2/services/affiliations';

import { Icon } from '@eduzz/ui-antd-theme';

import RecruitmentCommission from '../RecruitmentCommission';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  [EStatusAffiliation.APPROVED]: { label: 'Aprovada', color: 'success' },
  [EStatusAffiliation.PENDING]: { label: 'Pendente', color: 'purple' },
  [EStatusAffiliation.REPROVED]: { label: 'Reprovada', color: 'error' }
};

export const List = () => {
  const { message, modal } = App.useApp();
  const history = useHistory();

  const [isLoading] = useObservable(() => affiliationsService.isLoading(), []);
  const [affiliations] = useObservable(() => affiliationsService.getAffiliations(), []);
  const [pagination] = useObservable(() => affiliationsService.getPaginationResponse(), []);
  const [loadProductById] = useCallbackObservable((id: number) => affiliationsService.loadProductById(id), []);
  const [cancelAffiliation] = useCallbackObservable(
    (affiliation: IAffiliation) =>
      affiliationsService.cancelAffilation(affiliation).pipe(
        tap(
          () => {
            message.success('Afiliação cancelada com sucesso');
          },
          error => message.error(error instanceof Error ? error.message : 'Não foi possível cancelar a afiliação')
        )
      ),
    [message]
  );

  const [isModalRecruitmentOpen, setIsModalRecruitmentOpen] = useState<boolean>(false);

  const onChangeRowsPerPage = (perPage: number) => {
    affiliationsService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
    document.getElementById('affiliations-table').scrollIntoView();
  };

  const onChangePage = (page: number) => {
    affiliationsService.setPagination({ ...pagination, current_page: page });
  };

  const handleClickShare = (affiliation: IAffiliation) => {
    history.push(`/producer/campaigns/${affiliation.product_id}`);
  };

  const handleRecruitmentClick = (affiliation: IAffiliation) => {
    loadProductById(affiliation.product_id);
    setIsModalRecruitmentOpen(true);
  };

  const closeModalRecruitment = () => {
    setIsModalRecruitmentOpen(false);
  };

  const handleClickCancelAffiliation = (affiliation: IAffiliation) => {
    modal.confirm({
      title: 'Atenção',
      content: 'Deseja realmente cancelar a afiliação?',
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      cancelButtonProps: { type: 'text' },
      okButtonProps: { danger: true },
      onOk: () => cancelAffiliation(affiliation)
    });
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'affiliations_status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const config = STATUS_CONFIG[status] ?? { label: status, color: 'default' };
        return (
          <div className='flex flex-col items-start gap-1'>
            <Tag className='-me-2' color={config.color}>
              {config.label}
            </Tag>
          </div>
        );
      }
    },
    {
      title: 'Produto',
      dataIndex: 'product_title',
      key: 'product',
      width: 280,
      render: (_: string, aff: IAffiliation) => (
        <div className='flex flex-col items-start gap-1'>
          <Typography.Text>{aff.product_title}</Typography.Text>
          <Typography.Text type='secondary'>({aff.product_id})</Typography.Text>
          {aff.product_status === 'inactive' && (
            <Tag className='-me-2' color='error'>
              Inativo
            </Tag>
          )}
        </div>
      )
    },
    {
      title: 'Produtor',
      key: 'producer',
      width: 200,
      render: (_: any, aff: IAffiliation) => (
        <Link to={`/producer/detail/${aff.producer.id}`} target='_blank' rel='noopener noreferrer'>
          <Truncate count={25} final='...'>
            {aff.producer.business_name}
          </Truncate>
        </Link>
      )
    },
    {
      title: 'Recrutamento',
      key: 'recruitment',
      width: 130,
      render: (_: any, aff: IAffiliation) => (
        <Typography.Text type={aff.recruitment ? undefined : 'secondary'}>
          {aff.recruitment ? 'Habilitado' : 'Desabilitado'}
        </Typography.Text>
      )
    },
    { title: 'Recrutados', dataIndex: 'indications', key: 'indications', width: 100 },
    {
      title: 'Preço',
      key: 'price',
      width: 120,
      render: (_: any, aff: IAffiliation) => (
        <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores' fallback={<em>oculto</em>}>
          <Typography.Text type='secondary'>
            {aff.has_variations ? (
              <>
                máx.: <FormattedNumber value={aff.product_max_price} format='BRL' />
              </>
            ) : (
              <FormattedNumber value={aff.product_price} format='BRL' />
            )}
          </Typography.Text>
        </PermissionHide>
      )
    },
    {
      title: 'Comissão',
      key: 'commission',
      width: 260,
      render: (_: any, aff: IAffiliation) => (
        <Typography.Text type='secondary'>
          {aff.product_max_comission_charge_type !== 'G' && (
            <>
              <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores' fallback={<em>oculto</em>}>
                {aff.has_variations && !aff.has_differentiated_commission && <>máx: </>}
                <CalculateCommissionAffiliate
                  price={aff.product_price || 0}
                  commissionType={
                    aff.has_differentiated_commission
                      ? aff.differentiated_comission_type
                      : aff.product_max_comission_type
                  }
                  commissionValue={
                    aff.has_differentiated_commission ? aff.differentiated_comission_value : aff.product_max_comission
                  }
                  hasVariations={aff.has_variations}
                />
              </PermissionHide>
              &nbsp;de comissão {aff.product_max_comission_charge_type === 'A' ? 'recorrente' : null}
            </>
          )}
          {aff.product_max_comission_charge_type === 'G' && (
            <>
              <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores' fallback={<em>oculto</em>}>
                <CalculateCommissionAffiliate
                  price={aff.product_price ? aff.product_price : 0}
                  commissionType={aff.comission_type}
                  commissionValue={aff.comission}
                />
              </PermissionHide>
              &nbsp;de comissão
            </>
          )}
        </Typography.Text>
      )
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: unknown, aff: IAffiliation) => {
        const items: MenuProps['items'] = [];

        if (aff.affiliations_status?.toUpperCase() === EStatusAffiliation.APPROVED) {
          items.push({ key: 'share', label: 'Divulgar', onClick: () => handleClickShare(aff) });

          if (aff.recruitment) {
            items.push({ key: 'recruit', label: 'Recrutar', onClick: () => handleRecruitmentClick(aff) });
          }
        }

        items.push({
          key: 'cancel',
          label: 'Cancelar afiliação',
          onClick: () => handleClickCancelAffiliation(aff)
        });

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type='text' shape='circle' icon={<Icon.Ellipsis />} aria-label='Ações' />
          </Dropdown>
        );
      }
    }
  ];

  return (
    <>
      <Spin spinning={!!isLoading}>
        <Table
          size='small'
          sticky
          className='[&_.ant-table-tbody_.ant-typography]:text-sm'
          scroll={{ x: 'max-content' }}
          id='affiliations-table'
          dataSource={affiliations || []}
          columns={columns}
          rowKey='id'
          pagination={false}
          loading={isLoading}
        />
        {!isLoading && pagination && (
          <div className='mt-4 flex justify-end'>
            <Pagination
              current={pagination.current_page}
              pageSize={pagination.per_page}
              total={pagination.total_items || 0}
              onChange={onChangePage}
              onShowSizeChange={(_c: number, size: number) => onChangeRowsPerPage(size)}
              showSizeChanger
            />
          </div>
        )}
      </Spin>

      <RecruitmentCommission open={isModalRecruitmentOpen} closeModal={closeModalRecruitment} />
    </>
  );
};
